import { useEffect, useState } from 'react';
import {
  collection, onSnapshot, updateDoc, deleteDoc, doc, getDoc, setDoc,
  serverTimestamp, query, where, getDocs, limit, arrayUnion, orderBy,
} from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { createPortalUser } from '../../utils/createUser';
import useBasePath from '../../utils/useBasePath';
import { makeNote, normalizeLog } from './LeadDatabase';
import { useSalesFilters } from './salesFilters';
import { HOTELS, BOARD_TYPE_LABELS, ROOM_TYPE_LABELS, getHotelRate } from '../../utils/quoteHotelRates';
import { getCurrentWeekRates, toGBP } from '../../utils/exchangeRates';

// ─── Shared constants ─────────────────────────────────────────────────────────

export const CRM_STAGES = [
  'Tour request', 'Sent initial quote', 'Accepted initial quote',
  'Sent confirmed quote', 'Accepted', 'Moved to Tours',
];

const DEFAULT_FLOWS = ['Cold', 'Returner', 'Referral', 'Web Quote'];

const FALLBACK_GROUP_TYPES = [
  'School', 'College / 6th Form', 'University', 'Sports Club',
  'Corporate', 'Private Group', 'MIC Cup', 'Other',
];

const NIGHT_OPTIONS = [2, 3, 4, 7];

const FALLBACK_CATEGORIES = [
  'Hotel & Food', 'Flights', 'Transport', 'Match Tickets', 'Stadium Tour',
  'Fixtures', 'Training Sessions', 'Padel', 'Yoga', 'Waterpark',
];

const QUANTITY_ADDONS = {
  'Fixtures':          { max: 5, label: 'Fixtures per squad' },
  'Training Sessions': { max: 5, label: 'Training sessions per squad' },
  'Padel':             { max: 3 },
  'Yoga':              { max: 3 },
};

const STANDARD_PKG_NAMES = new Set(['Hotel & Food', 'Flights', 'Fixtures', 'Match Tickets', 'Training Sessions', 'Stadium Tour', 'Padel', 'Transport']);

const FLOWS_DOC = doc(db, 'settings', 'englishClubCrmFlows');

function getUserRoles(u) {
  if (Array.isArray(u.roles)) return u.roles;
  if (u.role) return [u.role];
  return [];
}

// ─── Small shared UI atoms (mirrors LeadDatabase.js) ───────────────────────────

function Label({ children, required }) {
  return (
    <label className="block text-[13px] font-medium text-[#374151] mb-1">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

function Input({ value, onChange, onBlur, placeholder, type = 'text' }) {
  return (
    <input type={type} value={value} onChange={onChange} onBlur={onBlur} placeholder={placeholder}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
    />
  );
}

function Sel({ value, onChange, children }) {
  return (
    <select value={value} onChange={onChange}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] bg-white"
    >
      {children}
    </select>
  );
}

function Stepper({ value, onChange }) {
  const n = Number(value) || 0;
  return (
    <div className="flex items-center">
      <button type="button" onClick={() => onChange(String(Math.max(0, n - 1)))}
        className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-[#F9FAFB] text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
      <input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]" />
      <button type="button" onClick={() => onChange(String(n + 1))}
        className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-[#F9FAFB] text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
    </div>
  );
}

function Modal({ onClose, title, badge, wide, children }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4">
      <div className={`bg-white rounded-[14px] shadow-xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} my-auto`}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-[15px] font-bold text-[#111827] truncate">{title}</p>
            {badge}
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151] transition text-xl leading-none flex-shrink-0">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function FlowBadge({ flow }) {
  return (
    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#EFF6FF] text-[#1A4A8A]">
      {flow || 'Cold'}
    </span>
  );
}

function useFlows() {
  const [flows, setFlows] = useState(DEFAULT_FLOWS);
  useEffect(() => {
    const unsub = onSnapshot(FLOWS_DOC, async (snap) => {
      if (snap.exists() && Array.isArray(snap.data().flows) && snap.data().flows.length) {
        setFlows(snap.data().flows);
      } else {
        setFlows(DEFAULT_FLOWS);
        try {
          const check = await getDoc(FLOWS_DOC);
          if (!check.exists()) await setDoc(FLOWS_DOC, { flows: DEFAULT_FLOWS });
        } catch { /* another staff member may have just seeded it */ }
      }
    }, () => setFlows(DEFAULT_FLOWS));
    return unsub;
  }, []);
  return flows;
}

function useLetterTemplates() {
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'letterTemplates'), (snap) => {
      setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setTemplates([]));
    return unsub;
  }, []);
  return templates;
}

function crmSlug(s) {
  return String(s || '').toLowerCase().replace(/\s+/g, '_');
}

// Mirrors functions/index.js's crmTriggerFor() plus the "Tour request" drip
// step triggers (crm_tour_request_<flow>_1/2/3) — used only to work out
// which stages/flows have an automated email configured, for the
// column-header automation preview. Keep in sync with that file if the
// trigger-naming scheme ever changes.
function crmTriggersForStage(stage, flow) {
  const f = crmSlug(flow);
  if (stage === 'Tour request') return [1, 2, 3].map((n) => `crm_tour_request_${f}_${n}`);
  if (stage === 'Sent initial quote') return [`crm_sent_initial_quote_${f}`];
  if (stage === 'Accepted initial quote') return [`crm_accepted_initial_quote_${f}`];
  if (stage === 'Sent confirmed quote') return [`crm_sent_confirmed_quote_${f}`];
  if (stage === 'Accepted') return [`crm_accepted_${f}`];
  return []; // 'Moved to Tours' — no automated email
}

function LightningIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path d="M11 1.5 3 11h5.2l-1.2 7.5L17 9h-5.2l1.2-7.5z" />
    </svg>
  );
}

function StageAutomationBolt({ stage, flows, templates }) {
  const [open, setOpen] = useState(false);
  const byFlow = flows
    .map((flow) => ({
      flow,
      matches: templates.filter((t) => t.enabled !== false && crmTriggersForStage(stage, flow).includes(t.trigger)),
    }))
    .filter((f) => f.matches.length > 0);

  if (byFlow.length === 0) return null;

  return (
    <>
      <button
        type="button" onClick={() => setOpen(true)}
        title="Automated email(s) configured for this stage — click to preview"
        className="text-[#D97706] hover:text-[#B45309] transition flex-shrink-0"
      >
        <LightningIcon className="w-3.5 h-3.5" />
      </button>
      {open && (
        <Modal onClose={() => setOpen(false)} title={`Automation — ${stage}`} wide>
          <div className="space-y-4">
            {byFlow.map(({ flow, matches }) => (
              <div key={flow}>
                <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">{flow}</p>
                {matches.map((t) => (
                  <div key={t.id} className="border border-[#E5E7EB] rounded-[8px] p-3 mb-2">
                    <p className="text-[13px] font-bold text-[#111827] mb-1.5">{t.subject || '(no subject)'}</p>
                    <p className="text-[12px] text-[#6B7280] whitespace-pre-wrap max-h-[160px] overflow-y-auto">{t.body || '(no body)'}</p>
                    {t.attachmentBody && (
                      <p className="text-[11px] text-[#9CA3AF] mt-2 italic">+ PDF attachment</p>
                    )}
                  </div>
                ))}
              </div>
            ))}
            <p className="text-[11px] text-[#9CA3AF]">
              Shown with merge tags unresolved ({'{{contact_name}}'} etc.) — the real email fills these in from
              each lead's actual details. To edit wording, go to Admin Settings → Letter Templates.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}

function useGroupTypes() {
  const [groupTypesList, setGroupTypesList] = useState(FALLBACK_GROUP_TYPES);
  useEffect(() => {
    getDocs(query(collection(db, 'groupTypes'))).then((snap) => {
      if (!snap.empty) {
        setGroupTypesList(snap.docs.map((d) => d.data()).filter((g) => g.active !== false).map((g) => g.name));
      }
    }).catch(() => {});
  }, []);
  return groupTypesList;
}

function useCategories() {
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES.map((name, i) => ({ id: name, name, order: i })));
  useEffect(() => {
    getDocs(query(collection(db, 'tourCategories'), orderBy('order', 'asc'))).then((snap) => {
      if (!snap.empty) setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }).catch(() => {});
  }, []);
  return categories;
}

function useStaffList() {
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    getDocs(query(collection(db, 'users'), limit(500))).then((snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => getUserRoles(u).some((r) => r === 'staff' || r === 'admin'))
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
      setStaff(list);
    }).catch(() => {});
  }, []);
  return staff;
}

// ─── Tour-shape fields — used once, in "Copy to CRM" ──────────────────────────
// (Once a record exists it's a real tour doc, editable via the normal Overview
// tab's own "Edit tour details" / "Edit group info" controls — no need to
// duplicate that editing surface here.)

const TOUR_FIELDS_EMPTY = {
  sport: '', destination: '', departureDate: '', returnDate: '',
  groupType: '', totalTravellers: '', numberOfSquads: '', brand: '',
};

// Padelante is a separate padel-only brand, not relevant to this UK-clubs
// lead source — the CRM only ever proceeds as one of these two.
const CRM_BRANDS = [
  { id: 'mst',       label: 'MST Sports Tours' },
  { id: 'your_tour', label: 'Your Tour' },
];

function validateTourFields(form) {
  const errs = {};
  if (!form.brand) errs.brand = 'Please choose which brand this lead will proceed as.';
  if (!form.sport.trim()) errs.sport = 'Sport is required.';
  if (!form.destination.trim()) errs.destination = 'Destination is required.';
  if (!form.departureDate) errs.departureDate = 'Departure date is required.';
  if (!form.returnDate) errs.returnDate = 'Return date is required.';
  if (form.returnDate && form.departureDate && form.returnDate < form.departureDate) {
    errs.returnDate = 'Return date must be after departure date.';
  }
  if (!form.groupType) errs.groupType = 'Group type is required.';
  if (!Number(form.totalTravellers)) {
    errs.totalTravellers = 'Please enter the total number of travellers.';
  }
  return errs;
}

function TourFieldsForm({ form, setForm, groupTypesList, errors }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  function applyNightPackage(nights) {
    if (!form.departureDate) return;
    const dep = new Date(form.departureDate);
    dep.setDate(dep.getDate() + nights);
    setForm((f) => ({ ...f, returnDate: dep.toISOString().slice(0, 10) }));
  }

  return (
    <div className="space-y-4">
      <div>
        <Label required>Brand this lead will proceed as</Label>
        <div className="flex gap-2">
          {CRM_BRANDS.map((b) => (
            <button
              key={b.id} type="button" onClick={() => setForm((f) => ({ ...f, brand: b.id }))}
              className={`flex-1 px-3 py-2.5 rounded-[8px] border text-[13px] font-semibold transition ${
                form.brand === b.id ? 'bg-[#1A4A8A] border-[#1A4A8A] text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-[#1A4A8A]/40'
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-1">Drives the branding on every quote, letter and automated email sent to this lead.</p>
        {errors.brand && <p className="text-[11px] text-red-600 mt-1">{errors.brand}</p>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label required>Sport</Label>
          <Input value={form.sport} onChange={set('sport')} placeholder="e.g. Football" />
          {errors.sport && <p className="text-[11px] text-red-600 mt-1">{errors.sport}</p>}
        </div>
        <div>
          <Label required>Destination</Label>
          <Input value={form.destination} onChange={set('destination')} placeholder="e.g. La Nucia" />
          {errors.destination && <p className="text-[11px] text-red-600 mt-1">{errors.destination}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label required>Departure date</Label>
          <Input type="date" value={form.departureDate} onChange={set('departureDate')} />
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-[11px] text-[#9CA3AF]">Quick:</span>
            {NIGHT_OPTIONS.map((n) => (
              <button
                key={n} type="button" onClick={() => applyNightPackage(n)} disabled={!form.departureDate}
                title={!form.departureDate ? 'Set a departure date first' : `Set return to ${n} nights after departure`}
                className="text-[11px] px-2.5 py-1 rounded-[6px] border border-[#E5E7EB] text-[#6B7280] hover:border-[#1A4A8A]/40 hover:text-[#1A4A8A] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {n} nights
              </button>
            ))}
          </div>
          {errors.departureDate && <p className="text-[11px] text-red-600 mt-1">{errors.departureDate}</p>}
        </div>
        <div>
          <Label required>Return date</Label>
          <Input type="date" value={form.returnDate} onChange={set('returnDate')} />
          {errors.returnDate && <p className="text-[11px] text-red-600 mt-1">{errors.returnDate}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label required>Group type</Label>
          <Sel value={form.groupType} onChange={set('groupType')}>
            <option value="">Select…</option>
            {groupTypesList.map((g) => <option key={g}>{g}</option>)}
          </Sel>
          {errors.groupType && <p className="text-[11px] text-red-600 mt-1">{errors.groupType}</p>}
        </div>
        <div>
          <Label>Number of squads</Label>
          <Input type="number" value={form.numberOfSquads} onChange={set('numberOfSquads')} placeholder="Optional" />
        </div>
      </div>

      <div>
        <Label required>Total number of travellers</Label>
        <Stepper value={form.totalTravellers} onChange={setVal('totalTravellers')} />
        <p className="text-[11px] text-[#9CA3AF] mt-1">Just a headcount for now — the player/supporter/coach breakdown can be added once this is in the CRM.</p>
        {errors.totalTravellers && <p className="text-[11px] text-red-600 mt-1">{errors.totalTravellers}</p>}
      </div>
    </div>
  );
}

// ─── What's included — same picker as the normal new-tour flow, so staff get
// the full category/quantity/notes UX right when a lead moves into CRM ───────

function WhatsIncludedPicker({ categories, packageMode, setPackageMode, selectedAddons, setSelectedAddons }) {
  function toggleAddon(cat) {
    setSelectedAddons((prev) => {
      if (prev[cat.id]) {
        const next = { ...prev };
        delete next[cat.id];
        return next;
      }
      const base = { name: cat.name, notes: '' };
      if (QUANTITY_ADDONS[cat.name]) return { ...prev, [cat.id]: { ...base, quantity: 1 } };
      if (cat.name === 'Flights') return { ...prev, [cat.id]: { ...base, largeCaseQuantity: 0 } };
      return { ...prev, [cat.id]: base };
    });
  }
  function updateAddonQuantity(catId, qty) {
    setSelectedAddons((prev) => ({ ...prev, [catId]: { ...prev[catId], quantity: qty } }));
  }
  function updateAddonNotes(catId, notes) {
    setSelectedAddons((prev) => ({ ...prev, [catId]: { ...prev[catId], notes } }));
  }
  function updateAddonLargeCaseQty(catId, qty) {
    setSelectedAddons((prev) => ({ ...prev, [catId]: { ...prev[catId], largeCaseQuantity: Math.max(0, qty) } }));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>What's included</Label>
        <div className="flex rounded-[8px] border border-[#E5E7EB] overflow-hidden text-[12px]">
          <button type="button" onClick={() => setPackageMode('standard')}
            className={`px-3 py-1.5 font-medium transition ${packageMode === 'standard' ? 'bg-[#1A4A8A] text-white' : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
            Standard package
          </button>
          <button type="button" onClick={() => setPackageMode('bespoke')}
            className={`px-3 py-1.5 font-medium border-l border-[#E5E7EB] transition ${packageMode === 'bespoke' ? 'bg-[#1A4A8A] text-white' : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
            Bespoke
          </button>
        </div>
      </div>
      <p className="text-[12px] text-[#9CA3AF]">
        {packageMode === 'standard'
          ? 'Pre-filled based on tour duration — edit freely or switch to Bespoke for full control.'
          : 'Select everything the customer wants included.'}
      </p>

      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => {
          const selected = !!selectedAddons[cat.id];
          return (
            <button
              key={cat.id} type="button" onClick={() => toggleAddon(cat)}
              className={`px-3.5 py-1.5 rounded-[20px] border text-[13px] font-medium transition ${
                selected ? 'bg-[#1A4A8A] border-[#1A4A8A] text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-[#1A4A8A]/40 hover:bg-[#EFF6FF]'
              }`}
            >
              {cat.name}
            </button>
          );
        })}
      </div>

      {Object.keys(selectedAddons).length > 0 && (
        <div className="space-y-4 pt-3 border-t border-[#F3F4F6]">
          <p className="text-[11px] font-medium text-[#9CA3AF] uppercase tracking-[0.9px]">Additional preferences</p>
          {Object.entries(selectedAddons).map(([id, val]) => {
            const qtyConfig = QUANTITY_ADDONS[val.name];

            if (qtyConfig) return (
              <div key={id}>
                <Label>{qtyConfig.label || val.name}</Label>
                <div className="flex gap-2">
                  {Array.from({ length: qtyConfig.max }, (_, i) => i + 1).map((n) => (
                    <button key={n} type="button" onClick={() => updateAddonQuantity(id, n)}
                      className={`w-10 h-10 rounded-[8px] text-[13px] font-semibold border transition ${val.quantity === n ? 'bg-[#1A4A8A] border-[#1A4A8A] text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-[#1A4A8A]/40'}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            );

            return (
              <div key={id} className="space-y-2">
                <Label>{val.name}</Label>
                {val.name === 'Flights' && (
                  <div className="flex items-center gap-3">
                    <span className="text-[13px] text-[#6B7280]">Large kit cases (group total)</span>
                    <button type="button" onClick={() => updateAddonLargeCaseQty(id, (val.largeCaseQuantity || 0) - 1)}
                      className="w-7 h-7 rounded-full border border-[#E5E7EB] text-[#374151] hover:border-[#1A4A8A]/40 flex items-center justify-center text-[16px] leading-none">−</button>
                    <span className="w-6 text-center text-[14px] font-semibold text-[#111827]">{val.largeCaseQuantity || 0}</span>
                    <button type="button" onClick={() => updateAddonLargeCaseQty(id, (val.largeCaseQuantity || 0) + 1)}
                      className="w-7 h-7 rounded-full border border-[#E5E7EB] text-[#374151] hover:border-[#1A4A8A]/40 flex items-center justify-center text-[16px] leading-none">+</button>
                    <span className="text-[11px] text-[#9CA3AF]">0 = hand luggage only</span>
                  </div>
                )}
                <Input value={val.notes} onChange={(e) => updateAddonNotes(id, e.target.value)} placeholder={`Specific requirements for ${val.name.toLowerCase()}…`} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── "Copy to CRM" — creates the real tour doc, opened from LeadDatabase.js ───

export function CopyToCrmModal({ lead, onClose, onSave }) {
  const [form, setForm] = useState(TOUR_FIELDS_EMPTY);
  const [initialNote, setInitialNote] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const groupTypesList = useGroupTypes();
  const categories = useCategories();
  const [packageMode, setPackageMode] = useState('standard');
  const [selectedAddons, setSelectedAddons] = useState({});

  // Pre-fill the standard package once dates are set, same as the normal
  // new-tour flow — editable freely, and re-applies if dates change.
  useEffect(() => {
    if (categories.length === 0 || packageMode !== 'standard') return;
    const nights = (() => {
      if (!form.departureDate || !form.returnDate) return 2;
      const diff = Math.round((new Date(form.returnDate) - new Date(form.departureDate)) / 86400000);
      return diff > 0 ? diff : 2;
    })();
    const fixtureQty = nights >= 3 ? 2 : 1;
    const defaults = {};
    categories.forEach((cat) => {
      if (!STANDARD_PKG_NAMES.has(cat.name)) return;
      const base = { name: cat.name, notes: '' };
      if (cat.name === 'Fixtures') defaults[cat.id] = { ...base, quantity: fixtureQty };
      else if (cat.name === 'Training Sessions') defaults[cat.id] = { ...base, quantity: 2 };
      else if (QUANTITY_ADDONS[cat.name]) defaults[cat.id] = { ...base, quantity: 1 };
      else if (cat.name === 'Flights') defaults[cat.id] = { ...base, largeCaseQuantity: 0 };
      else defaults[cat.id] = base;
    });
    setSelectedAddons(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageMode, form.departureDate, form.returnDate, categories]);

  async function handleSave() {
    const errs = validateTourFields(form);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    const addonsArray = Object.entries(selectedAddons).map(([id, val]) => ({
      categoryId: id,
      categoryName: val.name,
      notes: val.notes || '',
      ...(val.quantity !== undefined ? { quantity: val.quantity } : {}),
    }));
    const flightsAddon = Object.values(selectedAddons).find((v) => v.name === 'Flights');
    const largeCaseQuantity = flightsAddon ? Number(flightsAddon.largeCaseQuantity) || 0 : 0;
    await onSave({
      brand: form.brand,
      sport: form.sport.trim(),
      destination: form.destination.trim(),
      departureDate: form.departureDate,
      returnDate: form.returnDate,
      groupType: form.groupType,
      participants: Number(form.totalTravellers) || 0,
      numberOfSquads: form.numberOfSquads ? Number(form.numberOfSquads) : null,
      selectedAddons: addonsArray,
      largeCaseQuantity,
      initialNote: initialNote.trim(),
    });
    setSaving(false);
  }

  return (
    <Modal onClose={onClose} title={`Copy "${lead.clubSchool || lead.contactName}" to CRM`} wide>
      <div className="space-y-4">
        <div className="bg-[#F9FAFB] rounded-[8px] p-3 text-[13px] text-[#6B7280] space-y-0.5">
          <p className="font-semibold text-[#111827]">{lead.clubSchool || lead.contactName}</p>
          {lead.contactName && lead.clubSchool && <p>{lead.contactName}</p>}
          {(lead.email || lead.phone) && <p>{[lead.email, lead.phone].filter(Boolean).join(' · ')}</p>}
        </div>
        <p className="text-[12px] text-[#9CA3AF]">
          This creates a real tour record so you can start quoting and sending letters straight away — it'll show only Overview, Quote, Letters and Messages until it's converted at the end of the CRM pipeline.
        </p>
        <TourFieldsForm form={form} setForm={setForm} groupTypesList={groupTypesList} errors={errors} />
        <div className="pt-2 border-t border-[#F3F4F6]">
          <WhatsIncludedPicker
            categories={categories} packageMode={packageMode} setPackageMode={setPackageMode}
            selectedAddons={selectedAddons} setSelectedAddons={setSelectedAddons}
          />
        </div>
        <div>
          <Label>First note (optional)</Label>
          <textarea
            value={initialNote} onChange={(e) => setInitialNote(e.target.value)} rows={2}
            placeholder="Why this lead is being actively worked now…"
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] font-medium">Cancel</button>
          <button
            onClick={handleSave} disabled={saving}
            className="px-5 py-2.5 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition disabled:opacity-40"
          >
            {saving ? 'Copying…' : 'Copy to CRM'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Convert to tour — attaches a customer account, then unlocks full tabs ────
// Patches the SAME request doc (no second doc created) — everything else
// about the tour (destination, dates, quote, letters already sent…) stays.

function ConvertToTourModal({ request, requestId, currentUser, onClose, onConverted }) {
  const [step, setStep] = useState('customer'); // 'customer' | 'review'
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  const [newCustomer, setNewCustomer] = useState({ name: request.contactName || '', email: request.customerEmail || '', clubSchool: request.internalRef || '' });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [errors, setErrors] = useState({});
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    getDocs(query(collection(db, 'users'), where('role', '==', 'customer')))
      .then((snap) => setCustomers(snap.docs.map((d) => ({ uid: d.id, ...d.data() }))))
      .catch(() => {});
  }, []);

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    return c.email?.toLowerCase().includes(q) || c.name?.toLowerCase().includes(q);
  }).slice(0, 8);

  async function handleCreateCustomer() {
    const errs = {};
    if (!newCustomer.name.trim()) errs.newName = 'Name is required.';
    if (!newCustomer.email.trim()) errs.newEmail = 'Email is required.';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setCreatingCustomer(true);
    setErrors({});
    try {
      const { uid } = await createPortalUser({
        email: newCustomer.email, role: 'customer', name: newCustomer.name,
        clubSchool: newCustomer.clubSchool, createdByEmail: currentUser.email, brand: 'mst',
      });
      setSelectedCustomer({ uid, email: newCustomer.email.trim().toLowerCase(), name: newCustomer.name.trim() });
      setStep('review');
    } catch (err) {
      setErrors({
        createCustomer: err.code === 'auth/email-already-in-use'
          ? 'An account with this email already exists — search for it instead.'
          : 'Failed to create customer. Please try again.',
      });
    } finally {
      setCreatingCustomer(false);
    }
  }

  async function handleConfirm() {
    setConverting(true);
    try {
      await updateDoc(doc(db, 'requests', requestId), {
        customerId: selectedCustomer.uid,
        customerEmail: selectedCustomer.email,
        crmFullyConverted: true,
        // By the time a lead is converted it's been quoted and the dates
        // agreed through the CRM pipeline already — back-fill the Sales &
        // Admin status pipeline up to Date Confirmed. Deposit Paid is left
        // unticked since conversion doesn't itself mean a deposit was paid.
        stageProgress: {
          ...(request.stageProgress || {}),
          initial_interest: true, price_preparation: true, quote_sent: true,
          date_confirmed: true, deposit_paid: false,
        },
        status: 'quote_sent',
        updatedAt: serverTimestamp(),
      });
      onConverted();
    } catch (err) {
      setErrors({ confirm: 'Something went wrong converting this tour. Please try again.' });
      setConverting(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Convert to tour" wide>
      {step === 'customer' && (
        <div className="space-y-4">
          <div className="flex gap-2 border-b border-[#E5E7EB] pb-3">
            <button onClick={() => setMode('existing')}
              className={`px-3 py-1.5 rounded-[8px] text-[13px] font-semibold transition ${mode === 'existing' ? 'bg-[#1A4A8A] text-white' : 'bg-white border border-[#E5E7EB] text-[#374151]'}`}>
              Existing customer
            </button>
            <button onClick={() => setMode('new')}
              className={`px-3 py-1.5 rounded-[8px] text-[13px] font-semibold transition ${mode === 'new' ? 'bg-[#1A4A8A] text-white' : 'bg-white border border-[#E5E7EB] text-[#374151]'}`}>
              Create new customer
            </button>
          </div>

          {mode === 'existing' ? (
            <div>
              <Label>Search by name or email</Label>
              <Input value={search} onChange={(e) => { setSearch(e.target.value); setSelectedCustomer(null); }} placeholder="Search customers…" />
              {search && !selectedCustomer && (
                <div className="mt-2 border border-[#E5E7EB] rounded-[8px] divide-y divide-[#F3F4F6] max-h-48 overflow-y-auto">
                  {filtered.length === 0 ? (
                    <p className="text-[12px] text-[#9CA3AF] p-3">No matching customers.</p>
                  ) : filtered.map((c) => (
                    <button key={c.uid} onClick={() => setSelectedCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-[#F9FAFB] transition">
                      <p className="text-[13px] font-semibold text-[#111827]">{c.name || c.email}</p>
                      {c.name && <p className="text-[12px] text-[#9CA3AF]">{c.email}</p>}
                    </button>
                  ))}
                </div>
              )}
              {selectedCustomer && (
                <div className="mt-2 flex items-center justify-between bg-[#EFF6FF] border border-[#93C5FD] rounded-[8px] px-3 py-2">
                  <div>
                    <p className="text-[13px] font-semibold text-[#1A4A8A]">{selectedCustomer.name || selectedCustomer.email}</p>
                    {selectedCustomer.name && <p className="text-[12px] text-[#6B7280]">{selectedCustomer.email}</p>}
                  </div>
                  <button onClick={() => { setSelectedCustomer(null); setSearch(''); }} className="text-[#93C5FD] hover:text-[#1A4A8A] text-xl leading-none">×</button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label required>Name</Label>
                <Input value={newCustomer.name} onChange={(e) => setNewCustomer((c) => ({ ...c, name: e.target.value }))} placeholder="Full name" />
                {errors.newName && <p className="text-[11px] text-red-600 mt-1">{errors.newName}</p>}
              </div>
              <div>
                <Label required>Email</Label>
                <Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer((c) => ({ ...c, email: e.target.value }))} placeholder="name@email.com" />
                {errors.newEmail && <p className="text-[11px] text-red-600 mt-1">{errors.newEmail}</p>}
              </div>
              <div>
                <Label>Club / school</Label>
                <Input value={newCustomer.clubSchool} onChange={(e) => setNewCustomer((c) => ({ ...c, clubSchool: e.target.value }))} placeholder="Organisation name" />
              </div>
              {errors.createCustomer && <p className="text-[12px] text-red-600">{errors.createCustomer}</p>}
              <button
                onClick={handleCreateCustomer} disabled={creatingCustomer}
                className="px-4 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition disabled:opacity-40"
              >
                {creatingCustomer ? 'Creating…' : 'Create & select customer'}
              </button>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
            <button onClick={onClose} className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] font-medium">Cancel</button>
            <button
              onClick={() => selectedCustomer && setStep('review')}
              disabled={!selectedCustomer}
              className="px-5 py-2.5 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition disabled:opacity-40"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 'review' && (
        <div className="space-y-4">
          <div className="bg-[#F9FAFB] rounded-[8px] p-3 text-[13px] space-y-1">
            <p><span className="text-[#9CA3AF]">Customer:</span> <span className="font-semibold text-[#111827]">{selectedCustomer.name || selectedCustomer.email}</span> ({selectedCustomer.email})</p>
            <p><span className="text-[#9CA3AF]">Sport:</span> {request.sport}</p>
            <p><span className="text-[#9CA3AF]">Destination:</span> {request.destination}</p>
            <p><span className="text-[#9CA3AF]">Dates:</span> {request.departureDate} → {request.returnDate}</p>
          </div>
          <p className="text-[12px] text-[#9CA3AF]">This attaches the customer account and unlocks the full tour cockpit — nothing else about the tour changes.</p>
          {errors.confirm && <p className="text-[12px] text-red-600">{errors.confirm}</p>}
          <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
            <button onClick={() => setStep('customer')} className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] font-medium">Back</button>
            <button
              onClick={handleConfirm} disabled={converting}
              className="px-5 py-2.5 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition disabled:opacity-40"
            >
              {converting ? 'Converting…' : 'Convert to tour'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── CRM pipeline panel — rendered in RequestDetail.js's Overview tab ─────────

export function CrmPipelinePanel({ request, requestId, canManage }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const basePath = useBasePath();
  const flows = useFlows();
  const staff = useStaffList();
  const [converting, setConverting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [flightPriceDraft, setFlightPriceDraft] = useState(request.flightPriceManual || '');
  const [flightTimeDraft, setFlightTimeDraft] = useState(request.flightTimeManual || '');
  // These two are local drafts (so typing doesn't round-trip through Firestore
  // on every keystroke), seeded once at mount — unlike the Accommodation
  // selects below, which read `request.*` directly and so always show live
  // data. Without this, switching which quote is active via "Use this" left
  // both boxes frozen on whatever was last typed, regardless of which quote
  // was actually active — looked like flight price/time "staying on the
  // latest one added" no matter which option you switched to.
  useEffect(() => {
    setFlightPriceDraft(request.flightPriceManual || '');
    setFlightTimeDraft(request.flightTimeManual || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.activeQuoteAt]);

  // Accommodation — customers on /ytquote no longer pick a hotel/board/room
  // themselves (see Quote.js), so staff assign it here once the request
  // lands in the CRM. Only shown for requests that came through the quote
  // tool (they carry a hotelKey); recalculates estimateDetails' hotel cost
  // and total the same way Quote.js's own `calc` does.
  const [fxRates, setFxRates] = useState(null);
  const [priceCode, setPriceCode] = useState(null);
  useEffect(() => {
    if (!request.hotelKey) return;
    getCurrentWeekRates().then(setFxRates).catch(() => setFxRates(null));
    getDocs(query(collection(db, 'priceCodes'), where('name', '==', 'Online Quote'), limit(1)))
      .then((snap) => setPriceCode(snap.empty ? null : snap.docs[0].data()))
      .catch(() => setPriceCode(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.hotelKey]);
  const markupMultiplier = 1 + (Number(priceCode?.targetMargin) || 20) / 100;

  function patch(data) {
    return updateDoc(doc(db, 'requests', requestId), { ...data, updatedAt: serverTimestamp() });
  }
  function withNote(text, extra = {}) {
    const existing = normalizeLog(request.crmNoteLog);
    return { ...extra, crmNoteLog: [makeNote(text, currentUser, true), ...existing] };
  }
  function changeFlow(flow) {
    if (flow === (request.crmFlow || 'Cold')) return;
    patch(withNote(`Flow changed to ${flow}`, { crmFlow: flow }));
  }
  function changeStage(stage) {
    if (stage === request.crmStage) return;
    patch(withNote(`Stage changed to ${stage}`, { crmStage: stage }));
  }
  function changeAssignee(uid) {
    const a = staff.find((x) => x.id === uid);
    const name = a ? (a.name || a.email || '') : '';
    patch(withNote(uid ? `Assigned to ${name}` : 'Unassigned', { crmAssignedTo: uid, crmAssignedToName: name }));
  }

  async function handleDelete() {
    if (!window.confirm('Delete this CRM record? This cannot be undone.')) return;
    setDeleting(true);
    await deleteDoc(doc(db, 'requests', requestId));
    navigate(`${basePath}/team-database`);
  }

  // The accommodation/flight editors below only ever wrote the top-level
  // mirror fields, never the matching entry inside `quotes[]` — so an edit
  // never actually stuck to the quote it was made on. Switching "Use this"
  // to another option and back would reload the ORIGINAL frozen snapshot,
  // silently discarding whatever had just been edited. This folds the same
  // field changes into the active quote's own array entry so each quote
  // option can genuinely carry its own hotel/price/time.
  function withActiveQuoteSync(fields) {
    if (!Array.isArray(request.quotes) || !request.quotes.length) return fields;
    const idx = request.quotes.findIndex((q) => {
      const at = q.submittedAt?.toMillis ? q.submittedAt.toMillis() : Number(q.submittedAt) || 0;
      return request.activeQuoteAt && at === request.activeQuoteAt;
    });
    const activeIdx = idx >= 0 ? idx : request.quotes.length - 1;
    const quotes = [...request.quotes];
    const current = { ...quotes[activeIdx] };
    for (const [key, value] of Object.entries(fields)) {
      if (key.startsWith('estimateDetails.')) {
        current.estimateDetails = { ...(current.estimateDetails || {}), [key.slice('estimateDetails.'.length)]: value };
      } else {
        current[key] = value;
      }
    }
    quotes[activeIdx] = current;
    return { ...fields, quotes };
  }

  function applyAccommodation(nextHotelKey, nextBoardType, nextRoomType) {
    const hotel = HOTELS.find((h) => h.key === nextHotelKey) || HOTELS[0];
    const nights = Number(request.nights) || 0;
    const participants = Number(request.participants) || 0;
    const rate = getHotelRate(nextHotelKey, nextBoardType, nextRoomType, request.departureDate, nights, participants);

    const noteText = `Accommodation changed to ${hotel.name} · ${BOARD_TYPE_LABELS[nextBoardType] || nextBoardType} · ${ROOM_TYPE_LABELS[nextRoomType] || nextRoomType}`;
    const fields = { hotelKey: nextHotelKey, hotelName: hotel.name, boardType: nextBoardType, roomType: nextRoomType };

    // Only recompute the price when we actually have a live rate + FX for it
    // — otherwise leave the existing total untouched rather than zeroing it
    // out, and staff can price the stay manually on the cost sheet instead.
    if (rate.available && fxRates) {
      const hotelEUR = rate.isTotal ? rate.perPerson : rate.perPerson * nights;
      const newHotelGBP = toGBP(hotelEUR, 'EUR', fxRates) * markupMultiplier;
      const oldHotelGBP = Number(request.estimateDetails?.hotelGBP) || 0;
      const oldTotal = Number(request.estimateDetails?.total) || 0;
      const payingCount = Number(request.payingParticipants) || 1;
      const newTotal = oldTotal + (newHotelGBP - oldHotelGBP) * participants;
      fields['estimateDetails.hotelGBP'] = newHotelGBP;
      fields['estimateDetails.hotelCostEUR'] = hotelEUR;
      fields['estimateDetails.total'] = newTotal;
      fields['estimateDetails.perPerson'] = payingCount > 0 ? newTotal / payingCount : newTotal;
    }

    patch(withNote(noteText, withActiveQuoteSync(fields)));
  }
  function selectHotelKey(key) {
    const hotel = HOTELS.find((h) => h.key === key);
    if (!hotel) return;
    applyAccommodation(key, hotel.boardTypes[0], hotel.roomTypes[0]);
  }

  // Repeat /quote or /ytquote submissions from the same email fold onto this
  // one record server-side (see mergeInstantQuoteIntoExistingLead in
  // functions/index.js) instead of creating a separate lead each time — the
  // newest is mirrored to the top-level fields everything else here reads,
  // and every option (past + current) is kept in `quotes` so staff can pick
  // whichever one they actually want to proceed with.
  function makeQuoteActive(entry) {
    const { submittedAt, ...fields } = entry;
    patch(withNote(
      `Switched active quote to ${fields.hotelName || 'hotel TBC'} · ${fields.departureDate || 'date TBC'}`,
      { ...fields, activeQuoteAt: submittedAt?.toMillis ? submittedAt.toMillis() : Number(submittedAt) || Date.now() }
    ));
  }

  // Was display-only before — typing a fare here saved the text but never
  // touched estimateDetails.flightPP, so it silently never reached the quote
  // total. Parse the number out of it (same free-text field, "£145pp" etc.)
  // and apply the same delta-based recalculation applyAccommodation uses,
  // whenever this lead actually has a quote total to adjust.
  function saveFlightPrice() {
    const trimmed = flightPriceDraft.trim();
    if (trimmed === (request.flightPriceManual || '')) return;
    const fields = { flightPriceManual: trimmed };
    const match = trimmed.match(/[\d,]+(\.\d+)?/);
    const newFlightPP = match ? Number(match[0].replace(/,/g, '')) : null;
    if (newFlightPP != null && request.estimateDetails) {
      const oldFlightPP = Number(request.estimateDetails.flightPP) || 0;
      const oldTotal = Number(request.estimateDetails.total) || 0;
      const headcount = Number(request.participants) || 0;
      const payingCount = Number(request.payingParticipants) || 1;
      const newTotal = oldTotal + (newFlightPP - oldFlightPP) * headcount;
      fields['estimateDetails.flightPP'] = newFlightPP;
      fields['estimateDetails.total'] = newTotal;
      fields['estimateDetails.perPerson'] = payingCount > 0 ? newTotal / payingCount : newTotal;
    }
    patch(withActiveQuoteSync(fields));
  }
  function saveFlightTime() {
    if (flightTimeDraft === (request.flightTimeManual || '')) return;
    patch(withActiveQuoteSync({ flightTimeManual: flightTimeDraft.trim() }));
  }

  const canConvert = canManage && request.crmStage === 'Moved to Tours';

  return (
    <div className="bg-white rounded-[12px] border border-[#1A4A8A]/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold text-[#1A4A8A] uppercase tracking-[0.9px]">CRM pipeline</p>
        {canManage && (
          <button onClick={handleDelete} disabled={deleting} className="text-[11px] font-semibold text-[#DC2626] hover:underline disabled:opacity-40">
            {deleting ? 'Deleting…' : 'Delete record'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Flow</Label>
          <Sel value={request.crmFlow || 'Cold'} onChange={(e) => changeFlow(e.target.value)}>
            {flows.map((f) => <option key={f}>{f}</option>)}
          </Sel>
        </div>
        <div>
          <Label>Stage</Label>
          <Sel value={request.crmStage || CRM_STAGES[0]} onChange={(e) => changeStage(e.target.value)}>
            {CRM_STAGES.map((s) => <option key={s}>{s}</option>)}
          </Sel>
        </div>
        <div>
          <Label>Assigned to</Label>
          <Sel value={request.crmAssignedTo || ''} onChange={(e) => changeAssignee(e.target.value)}>
            <option value="">Unassigned</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
          </Sel>
        </div>
      </div>

      {request.hotelKey && (() => {
        const currentHotel = HOTELS.find((h) => h.key === request.hotelKey) || HOTELS[0];
        const currentRoomType = request.roomType || currentHotel.roomTypes[0];
        const currentBoardType = request.boardType || currentHotel.boardTypes[0];
        return (
          <div className="pt-1">
            <Label>Accommodation</Label>
            <div className={`grid gap-3 ${currentHotel.boardTypes.length > 1 ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <Sel value={request.hotelKey} onChange={(e) => selectHotelKey(e.target.value)}>
                {HOTELS.map((h) => <option key={h.key} value={h.key}>{h.name}</option>)}
              </Sel>
              {currentHotel.boardTypes.length > 1 && (
                <Sel value={currentBoardType} onChange={(e) => applyAccommodation(request.hotelKey, e.target.value, currentRoomType)}>
                  {currentHotel.boardTypes.map((bt) => <option key={bt} value={bt}>{BOARD_TYPE_LABELS[bt]}</option>)}
                </Sel>
              )}
              <Sel value={currentRoomType} onChange={(e) => applyAccommodation(request.hotelKey, currentBoardType, e.target.value)}>
                {currentHotel.roomTypes.map((rt) => <option key={rt} value={rt}>{ROOM_TYPE_LABELS[rt]}</option>)}
              </Sel>
            </div>
            <p className="text-[11px] text-[#9CA3AF] mt-1.5">
              {request.estimateDetails?.hotelGBP > 0
                ? `£${Number(request.estimateDetails.hotelGBP).toFixed(2)} pp — changing this updates the quote total automatically.`
                : 'No live rate for this combination/date — update the cost sheet manually if needed.'}
            </p>
          </div>
        );
      })()}

      {Array.isArray(request.quotes) && request.quotes.length > 1 && (
        <div className="pt-1">
          <Label>Quote options ({request.quotes.length})</Label>
          <div className="space-y-1.5">
            {request.quotes.map((q, i) => {
              const at = q.submittedAt?.toMillis ? q.submittedAt.toMillis() : Number(q.submittedAt) || 0;
              const isActive = request.activeQuoteAt ? at === request.activeQuoteAt : i === request.quotes.length - 1;
              return (
                <div key={i} className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-[8px] text-[12px] border ${isActive ? 'bg-[#EFF6FF] border-[#BFDBFE]' : 'bg-[#F9FAFB] border-[#F3F4F6]'}`}>
                  <span className="text-[#374151]">
                    {q.departureDate || '—'} · {q.hotelName || '—'} · {q.participants || 0} pax
                    {q.estimateDetails?.perPerson > 0 ? ` · £${Math.round(q.estimateDetails.perPerson)}pp` : ''}
                  </span>
                  {isActive
                    ? <span className="text-[10px] font-semibold text-[#1A4A8A] uppercase flex-shrink-0">Active</span>
                    : <button onClick={() => makeQuoteActive(q)} className="text-[11px] font-semibold text-[#1A4A8A] hover:underline flex-shrink-0">Use this</button>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Flights aren't priced on the public quote tool — staff fill these in
          once they've got a real fare from the flight operator. */}
      <div className="grid grid-cols-2 gap-3 pt-1">
        <div>
          <Label>Flight price</Label>
          <Input value={flightPriceDraft} onChange={(e) => setFlightPriceDraft(e.target.value)} onBlur={saveFlightPrice} placeholder="e.g. £145pp" />
        </div>
        <div>
          <Label>Flight time</Label>
          <Input value={flightTimeDraft} onChange={(e) => setFlightTimeDraft(e.target.value)} onBlur={saveFlightTime} placeholder="e.g. 06:35 outbound" />
        </div>
      </div>
      <p className="text-[11px] text-[#9CA3AF] -mt-1">
        Saves automatically when you click away — no separate save button.
        {request.estimateDetails ? ' The number in "Flight price" updates the quote total too.' : ''}
      </p>

      {canConvert && (
        <div className="pt-3 border-t border-[#F3F4F6]">
          <button onClick={() => setConverting(true)} className="px-4 py-2 bg-[#065F46] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#054435] transition">
            Convert to tour
          </button>
          <p className="text-[11px] text-[#9CA3AF] mt-1.5">Attaches a customer account and unlocks the full tour cockpit.</p>
        </div>
      )}

      {converting && (
        <ConvertToTourModal
          request={request}
          requestId={requestId}
          currentUser={currentUser}
          onClose={() => setConverting(false)}
          onConverted={() => setConverting(false)}
        />
      )}
    </div>
  );
}

// ─── Main CRM list — a filtered view of `requests` where crmOrigin is true ────

// ─── Kanban card ────────────────────────────────────────────────────────────

function CrmCard({ record, basePath, onMoveStage }) {
  return (
    <div className="bg-white rounded-[10px] border border-[#E5E7EB] p-3 flex flex-col text-left hover:border-[#93C5FD] hover:shadow-sm transition">
      <Link to={`${basePath}/requests/${record.id}`} className="block">
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="text-[13px] font-bold text-[#111827] pr-1 flex-1 min-w-0">{record.internalRef || record.contactName || 'Untitled'}</p>
          {record.crmFullyConverted && <span className="px-1.5 py-0.5 bg-[#ECFDF5] text-[#065F46] rounded-full text-[10px] font-semibold uppercase tracking-wide flex-shrink-0">Converted</span>}
        </div>
        <FlowBadge flow={record.crmFlow} />
        {Array.isArray(record.quotes) && record.quotes.length > 1 && (
          <span className="inline-block mt-1 px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded-full text-[10px] font-semibold uppercase tracking-wide">
            {record.quotes.length} quote options
          </span>
        )}
        <p className="text-[11.5px] text-[#6B7280] mt-1.5">{record.destination} · {record.departureDate} → {record.returnDate}</p>
        <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">{record.participants || 0} travellers</p>
        <p className="text-[11px] text-[#9CA3AF] mt-1.5 truncate">
          {record.crmAssignedToName ? `Assigned: ${record.crmAssignedToName}` : 'Unassigned'}
        </p>
      </Link>
      {!record.crmFullyConverted && (
        <select
          value={record.crmStage || CRM_STAGES[0]}
          onChange={(e) => onMoveStage(record, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="mt-2 w-full border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[11px] text-[#374151] bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
        >
          {CRM_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
    </div>
  );
}

// ─── Main CRM view — a Kanban board of `requests` where crmOrigin is true ─────

export default function CrmDatabase({ canManage = true, basePath = '/staff' }) {
  const { currentUser } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const staff = useStaffList();
  const flows = useFlows();
  const templates = useLetterTemplates();

  const f = useSalesFilters(currentUser?.uid);
  const [flowFilter, setFlowFilter] = useState('');
  const [addingFlow, setAddingFlow] = useState(false);
  const [newFlow, setNewFlow] = useState('');

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'requests'), where('crmOrigin', '==', true)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        setRecords(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  const filtered = records.filter((r) => {
    if (flowFilter && (r.crmFlow || 'Cold') !== flowFilter) return false;
    return f.matches({
      status: r.crmStage || CRM_STAGES[0],
      assignedTo: r.crmAssignedTo,
      hasContact: Boolean(r.customerEmail || r.phone),
      haystack: [r.internalRef, r.contactName, r.customerEmail, r.phone, r.destination].filter(Boolean).join(' '),
    });
  });

  function moveStage(record, stage) {
    if (stage === record.crmStage) return;
    const existing = normalizeLog(record.crmNoteLog);
    updateDoc(doc(db, 'requests', record.id), {
      crmStage: stage,
      crmNoteLog: [makeNote(`Stage changed to ${stage}`, currentUser, true), ...existing],
      updatedAt: serverTimestamp(),
    });
  }

  async function addFlow() {
    const name = newFlow.trim();
    if (!name || flows.includes(name)) { setNewFlow(''); setAddingFlow(false); return; }
    await updateDoc(FLOWS_DOC, { flows: arrayUnion(name) }).catch(async () => {
      await setDoc(FLOWS_DOC, { flows: arrayUnion(name) }, { merge: true });
    });
    setNewFlow('');
    setAddingFlow(false);
  }

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-[#6B7280]">
        Leads MST is actively working — {records.length} record(s){filtered.length !== records.length ? ` · ${filtered.length} shown` : ''}
      </p>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={f.search} onChange={(e) => f.setSearch(e.target.value)} placeholder="Search…"
          className="border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] w-[200px]"
        />
        <Sel value={flowFilter} onChange={(e) => setFlowFilter(e.target.value)}>
          <option value="">Any flow</option>
          {flows.map((flow) => <option key={flow} value={flow}>{flow}</option>)}
        </Sel>
        <Sel value={f.assignee} onChange={(e) => f.setAssignee(e.target.value)}>
          <option value="">Anyone assigned</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
        </Sel>

        {canManage && (addingFlow ? (
          <span className="flex items-center gap-1">
            <input
              autoFocus value={newFlow} onChange={(e) => setNewFlow(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addFlow()}
              placeholder="New flow name"
              className="border border-[#E5E7EB] rounded-[8px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
            />
            <button onClick={addFlow} className="text-[12px] font-semibold text-[#1A4A8A] hover:underline">Add</button>
            <button onClick={() => { setAddingFlow(false); setNewFlow(''); }} className="text-[12px] text-[#9CA3AF] hover:underline">Cancel</button>
          </span>
        ) : (
          <button onClick={() => setAddingFlow(true)} className="text-[12px] font-semibold text-[#1A4A8A] hover:underline">+ Add flow</button>
        ))}
      </div>

      {/* Kanban board — columns always show, even with nothing in them yet,
          so the pipeline shape is visible from the first visit. */}
      {loading ? (
        <p className="text-[#9CA3AF] text-[14px]">Loading CRM records…</p>
      ) : (
        <>
          {records.length === 0 && (
            <p className="text-[12px] text-[#9CA3AF]">Copy a lead into the CRM from the Leads tab to get started.</p>
          )}
          <div className="flex gap-4 overflow-x-auto pb-2">
          {CRM_STAGES.map((stage) => {
            const cards = filtered.filter((r) => (r.crmStage || CRM_STAGES[0]) === stage);
            return (
              <div key={stage} className="flex-shrink-0 w-[270px] flex flex-col">
                <div className="flex items-center justify-between px-1 mb-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-[12px] font-semibold text-[#374151] truncate">{stage}</p>
                    <StageAutomationBolt stage={stage} flows={flows} templates={templates} />
                  </div>
                  <span className="text-[11px] text-[#9CA3AF] flex-shrink-0">{cards.length}</span>
                </div>
                <div className="bg-[#F3F4F6] rounded-[12px] p-2 space-y-2 min-h-[80px] max-h-[70vh] overflow-y-auto">
                  {cards.length === 0 ? (
                    <p className="text-[11px] text-[#9CA3AF] text-center py-4">No records</p>
                  ) : cards.map((record) => (
                    <CrmCard key={record.id} record={record} basePath={basePath} onMoveStage={moveStage} />
                  ))}
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
}
